import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Legal from "@/pages/Legal";
import Maintenance from "@/pages/Maintenance";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page text-inkmuted">
        <span className="animate-pulse font-serif text-2xl">Lädt…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

/**
 * Wrappt die öffentlichen Routen (nicht /admin*): prüft beim Laden den Wartungsmodus und zeigt
 * bei Bedarf den Sperrbildschirm statt der Kinder. Reagiert außerdem live auf 503-Antworten
 * ("Website in Arbeit") von beliebigen API-Aufrufen, z. B. wenn ein Wartungs-/Admin-Token
 * zwischenzeitlich abläuft.
 */
function MaintenanceGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/maintenance")
      .then((r) => {
        if (cancelled) return;
        const active = !!r.data?.active;
        const hasBypass = !!localStorage.getItem("ton_maint") || !!localStorage.getItem("ton_token");
        setBlocked(active && !hasBypass);
      })
      .catch(() => { if (!cancelled) setBlocked(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 503 && err.response?.data?.detail === "Website in Arbeit") {
          localStorage.removeItem("ton_maint");
          setBlocked(true);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  if (loading) return null;
  if (blocked) return <Maintenance />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<MaintenanceGate><Landing /></MaintenanceGate>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/impressum" element={<MaintenanceGate><Legal kind="impressum" /></MaintenanceGate>} />
            <Route path="/datenschutz" element={<MaintenanceGate><Legal kind="datenschutz" /></MaintenanceGate>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
